// Source colours, per-row source mix and the legend for the Dashboard's
// Campaign & Source Performance card. Everything is DERIVED from the rows the
// server returns, so a new source (or a stored value nobody planned for) shows
// up with no code change. The card used to hardcode three legend entries and a
// "Facebook / Google Sheets / else red" dot, which hid TikTok entirely: TikTok
// leads fell into a row whose dominant source was Google Ads.
import { leadSourceColor } from "./leadSources";

var PALETTE = ["#F97316", "#14B8A6", "#A855F7", "#84CC16", "#EC4899", "#06B6D4", "#EAB308", "#6366F1"];

// A source in the lead source registry (src/lib/leadSources.js — the server's
// list, 2026-11-02) takes its registry colour; anything else gets a STABLE colour
// from its name, so the same source is the same colour in the legend, on every
// row and across reloads.
export function sourceColor(name) {
  var key = String(name || "").trim().toLowerCase();
  if (!key) return "#94A3B8";
  var fixed = leadSourceColor(key);
  if (fixed) return fixed;
  var h = 0;
  for (var i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

// One row's sources, largest first. Falls back to the dominant `topSource`
// when the server predates the `sources` map, so the card never goes blank
// during the window between a frontend and a backend deploy.
export function rowSourceMix(c) {
  var src = c && c.sources && typeof c.sources === "object" ? c.sources : null;
  var out = [];
  if (src) {
    Object.keys(src).forEach(function (k) { if (src[k] > 0) out.push({ source: k, leads: src[k], color: sourceColor(k) }); });
  } else if (c && c.topSource) {
    out.push({ source: c.topSource, leads: c.leads || 0, color: sourceColor(c.topSource) });
  }
  return out.sort(function (a, b) { return b.leads - a.leads; });
}

// The card's legend: the server's cohort-wide `sources` list when present,
// otherwise summed from every row's mix. Largest first.
export function sourceLegend(serverSources, rows) {
  var list = [];
  if (Array.isArray(serverSources)) {
    list = serverSources.filter(function (s) { return s && s.source; })
      .map(function (s) { return { source: s.source, leads: s.leads || 0, color: sourceColor(s.source) }; });
  } else {
    var tot = {};
    (rows || []).forEach(function (c) { rowSourceMix(c).forEach(function (m) { tot[m.source] = (tot[m.source] || 0) + m.leads; }); });
    list = Object.keys(tot).map(function (k) { return { source: k, leads: tot[k], color: sourceColor(k) }; });
  }
  return list.sort(function (a, b) { return b.leads - a.leads; });
}

// ---- "Other campaigns (N)" roll-up -----------------------------------------
// The card lists the 8 biggest campaigns by lead volume; the conversions sit in
// the small ones, so without a roll-up the visible rows read 0 EOI / Deals while
// the cohort total is not. This sums the remaining rows' OWN fields (no new
// definition of a deal or a conversion) into one raw-shaped row.
//
// The shares are the one thing the server cannot send for a sum, so this is a
// TWIN of server.js `sharePct` (same rounding), run against the server copy by
// verify-metric-definitions so the two cannot drift.
export function sharePctTwin(n, base) {
  if (!base || base <= 0 || !n || n <= 0) return 0;
  var whole = Math.round(n / base * 100);
  if (whole > 0) return whole;
  return Math.round(n / base * 1000) / 10;
}

export function otherCampaignsRow(rest, label) {
  if (!rest || !rest.length) return null;
  var o = { campaign: label || ("Other campaigns (" + rest.length + ")"), unitTypes: [], leads: 0, interested: 0, meetings: 0,
            eoisReached: 0, deals: 0, contacted: 0, sources: {} };
  rest.forEach(function (c) {
    ["leads", "interested", "meetings", "eoisReached", "deals", "contacted"].forEach(function (k) { o[k] += (c[k] || 0); });
    rowSourceMix(c).forEach(function (m) { o.sources[m.source] = (o.sources[m.source] || 0) + m.leads; });
  });
  o.intPct = sharePctTwin(o.interested, o.leads);
  o.meetPct = sharePctTwin(o.meetings, o.leads);
  o.eoiPct = sharePctTwin(o.eoisReached, o.leads);
  o.convPct = sharePctTwin(o.deals, o.leads);
  return o;
}

// A campaign-name click on the Dashboard card opens the Leads page on EXACTLY the
// leads that row counted: the same `reportDrill` spec the Reports > Campaigns >
// Performance tab sends (development axis, cohort = leads created in the window).
// The server resolves `devGroup` with the card's own grouping, so the list and the
// row cannot disagree. An empty devGroup is the "No campaign / Manual" row. Pure and
// at module scope so verify-report-drilldown can run it through tileListUrl.
export function campaignDrillSpec(row, range, periodLabel) {
  var manual = !!(row && row.manual);
  var name = manual ? "No campaign / Manual" : String((row && row.campaign) || "");
  return {
    type: "reportDrill",
    devGroup: manual ? "" : name,
    periodFrom: new Date(range.from).toISOString(),
    periodTo: new Date(range.to).toISOString(),
    periodLabel: periodLabel,
    label: "Development: " + name
  };
}
